// Angular mirror of TaskCodeRegistry.java (backend)
// Integer codes use gap-10 spacing to allow mid-domain insertion without renumbering.

export class TaskCodeRegistry {

  // SUB_STATUS — operational granularity beyond the high-level status integer
  static readonly SUB_STATUS_TRIAGED            = 10;
  static readonly SUB_STATUS_EN_ROUTE           = 20;
  static readonly SUB_STATUS_ON_SITE            = 30;
  static readonly SUB_STATUS_DIAGNOSED          = 40;
  static readonly SUB_STATUS_AWAITING_PARTS     = 50;
  static readonly SUB_STATUS_AWAITING_VENDOR    = 60;
  static readonly SUB_STATUS_RESOLVED           = 70;
  static readonly SUB_STATUS_VERIFIED_BY_GUEST  = 80;
  static readonly SUB_STATUS_ARRIVED            = 90;
  static readonly SUB_STATUS_CLEANING           = 100;

  // PRIORITY
  static readonly PRIORITY_P0_EMERGENCY = 10;
  static readonly PRIORITY_P1_URGENT    = 20;
  static readonly PRIORITY_P2_NORMAL    = 30;
  static readonly PRIORITY_P3_LOW       = 40;

  // BILLABLE
  static readonly BILLABLE_OWNER           = 10;
  static readonly BILLABLE_KBM             = 20;
  static readonly BILLABLE_GUEST           = 30;
  static readonly BILLABLE_VENDOR_WARRANTY = 40;

  // CAUSE
  static readonly CAUSE_GUEST_CAUSED   = 10;
  static readonly CAUSE_PROPERTY_DEFECT = 20;
  static readonly CAUSE_WEAR_AND_TEAR  = 30;
  static readonly CAUSE_VENDOR_NEGLIGENCE = 40;
  static readonly CAUSE_ACT_OF_GOD     = 50;
  static readonly CAUSE_UNKNOWN        = 60;

  // OWNER_APPROVAL
  static readonly OWNER_APPROVAL_NOT_REQUIRED = 10;
  static readonly OWNER_APPROVAL_PENDING      = 20;
  static readonly OWNER_APPROVAL_APPROVED     = 30;
  static readonly OWNER_APPROVAL_DECLINED     = 40;

  // LINE_TYPE — task line item types
  static readonly LINE_TYPE_LABOR       = 10;
  static readonly LINE_TYPE_SUPPLY      = 20;
  static readonly LINE_TYPE_SUBCONTRACT = 30;
  static readonly LINE_TYPE_OTHER       = 40;

  // SUPPLY_SOURCE
  static readonly SUPPLY_SOURCE_WAREHOUSE       = 10;
  static readonly SUPPLY_SOURCE_VENDOR_TRUCK    = 20;
  static readonly SUPPLY_SOURCE_PURCHASED_ONSITE = 30;
  static readonly SUPPLY_SOURCE_OTHER           = 40;

  // VENDOR_PAY — vendor payment status
  static readonly VENDOR_PAY_UNPAID    = 10;
  static readonly VENDOR_PAY_SCHEDULED = 20;
  static readonly VENDOR_PAY_PAID      = 30;

  // EVENT — task status event types
  static readonly EVENT_STARTED             = 10;
  static readonly EVENT_PAUSED              = 20;
  static readonly EVENT_RESUMED             = 30;
  static readonly EVENT_COMPLETED           = 40;
  static readonly EVENT_REOPENED            = 50;
  static readonly EVENT_CANCELLED           = 60;
  static readonly EVENT_TRIAGED             = 70;
  static readonly EVENT_ON_SITE             = 80;
  static readonly EVENT_DIAGNOSED           = 90;
  static readonly EVENT_AWAITING_PARTS      = 100;
  static readonly EVENT_AWAITING_VENDOR     = 110;
  static readonly EVENT_RESOLVED            = 120;
  static readonly EVENT_VERIFIED_BY_GUEST   = 130;
  static readonly EVENT_ASSIGNMENT_ACCEPTED = 140;
  static readonly EVENT_ASSIGNMENT_DECLINED = 150;

  // SOURCE — provenance during Breezeway transition
  static readonly SOURCE_INTERNAL  = 10;
  static readonly SOURCE_BREEZEWAY = 20;
  static readonly SOURCE_BACKFILL  = 30;

  // PHOTO
  static readonly PHOTO_BEFORE    = 10;
  static readonly PHOTO_DURING    = 20;
  static readonly PHOTO_AFTER     = 30;
  static readonly PHOTO_REFERENCE = 40;
  static readonly PHOTO_EVIDENCE  = 50;
  static readonly PHOTO_RECEIPT   = 60;

  // REGION
  static readonly REGION_HAWAII = 10;
  static readonly REGION_UTAH   = 20;
  static readonly REGION_BOTH   = 30;

  // VENDOR_TYPE
  static readonly VENDOR_TYPE_HOUSEKEEPING  = 10;
  static readonly VENDOR_TYPE_MAINTENANCE   = 20;
  static readonly VENDOR_TYPE_INSPECTOR     = 30;
  static readonly VENDOR_TYPE_LANDSCAPING   = 40;
  static readonly VENDOR_TYPE_PEST_CONTROL  = 50;
  static readonly VENDOR_TYPE_POOL_SPA      = 60;
  static readonly VENDOR_TYPE_HVAC          = 70;
  static readonly VENDOR_TYPE_PLUMBING      = 80;
  static readonly VENDOR_TYPE_ELECTRICAL    = 90;
  static readonly VENDOR_TYPE_APPLIANCE     = 100;
  static readonly VENDOR_TYPE_LOCKSMITH     = 110;
  static readonly VENDOR_TYPE_LAUNDRY       = 120;
  static readonly VENDOR_TYPE_OTHER         = 130;

  // PAYMENT — payment method
  static readonly PAYMENT_ACH         = 10;
  static readonly PAYMENT_CHECK       = 20;
  static readonly PAYMENT_CREDIT_CARD = 30;
  static readonly PAYMENT_OTHER       = 40;

  // TRIGGER — template trigger types (map to int_1 slot on task per playbook §11.2)
  static readonly TRIGGER_RESERVATION_CHECKOUT        = 1;
  static readonly TRIGGER_RESERVATION_CHECKIN_T_MINUS = 2;
  static readonly TRIGGER_OCCUPANCY_THRESHOLD         = 3;
  static readonly TRIGGER_GUEST_PORTAL_REQUEST        = 4;
  static readonly TRIGGER_INSPECTION_FAILED           = 5;
  static readonly TRIGGER_MANUAL                      = 6;
  static readonly TRIGGER_RECURRING_CALENDAR          = 7;
  static readonly TRIGGER_TASK_CHAIN                  = 8;
}

// --- Domain label maps ---

const SUB_STATUS_LABELS: Record<number, string> = {
  [TaskCodeRegistry.SUB_STATUS_TRIAGED]:           'Triaged',
  [TaskCodeRegistry.SUB_STATUS_EN_ROUTE]:          'En Route',
  [TaskCodeRegistry.SUB_STATUS_ON_SITE]:           'On Site',
  [TaskCodeRegistry.SUB_STATUS_DIAGNOSED]:         'Diagnosed',
  [TaskCodeRegistry.SUB_STATUS_AWAITING_PARTS]:    'Awaiting Parts',
  [TaskCodeRegistry.SUB_STATUS_AWAITING_VENDOR]:   'Awaiting Vendor',
  [TaskCodeRegistry.SUB_STATUS_RESOLVED]:          'Resolved',
  [TaskCodeRegistry.SUB_STATUS_VERIFIED_BY_GUEST]: 'Verified by Guest',
  [TaskCodeRegistry.SUB_STATUS_ARRIVED]:           'Arrived',
  [TaskCodeRegistry.SUB_STATUS_CLEANING]:          'Cleaning',
};

const PRIORITY_LABELS: Record<number, string> = {
  [TaskCodeRegistry.PRIORITY_P0_EMERGENCY]: 'P0 Emergency',
  [TaskCodeRegistry.PRIORITY_P1_URGENT]:    'P1 Urgent',
  [TaskCodeRegistry.PRIORITY_P2_NORMAL]:    'P2 Normal',
  [TaskCodeRegistry.PRIORITY_P3_LOW]:       'P3 Low',
};

const BILLABLE_LABELS: Record<number, string> = {
  [TaskCodeRegistry.BILLABLE_OWNER]:           'Owner',
  [TaskCodeRegistry.BILLABLE_KBM]:             'KBM',
  [TaskCodeRegistry.BILLABLE_GUEST]:           'Guest',
  [TaskCodeRegistry.BILLABLE_VENDOR_WARRANTY]: 'Vendor Warranty',
};

const CAUSE_LABELS: Record<number, string> = {
  [TaskCodeRegistry.CAUSE_GUEST_CAUSED]:       'Guest Caused',
  [TaskCodeRegistry.CAUSE_PROPERTY_DEFECT]:    'Property Defect',
  [TaskCodeRegistry.CAUSE_WEAR_AND_TEAR]:      'Wear and Tear',
  [TaskCodeRegistry.CAUSE_VENDOR_NEGLIGENCE]:  'Vendor Negligence',
  [TaskCodeRegistry.CAUSE_ACT_OF_GOD]:         'Act of God',
  [TaskCodeRegistry.CAUSE_UNKNOWN]:            'Unknown',
};

const OWNER_APPROVAL_LABELS: Record<number, string> = {
  [TaskCodeRegistry.OWNER_APPROVAL_NOT_REQUIRED]: 'Not Required',
  [TaskCodeRegistry.OWNER_APPROVAL_PENDING]:       'Pending',
  [TaskCodeRegistry.OWNER_APPROVAL_APPROVED]:      'Approved',
  [TaskCodeRegistry.OWNER_APPROVAL_DECLINED]:      'Declined',
};

const LINE_TYPE_LABELS: Record<number, string> = {
  [TaskCodeRegistry.LINE_TYPE_LABOR]:       'Labor',
  [TaskCodeRegistry.LINE_TYPE_SUPPLY]:      'Supply',
  [TaskCodeRegistry.LINE_TYPE_SUBCONTRACT]: 'Subcontract',
  [TaskCodeRegistry.LINE_TYPE_OTHER]:       'Other',
};

const SUPPLY_SOURCE_LABELS: Record<number, string> = {
  [TaskCodeRegistry.SUPPLY_SOURCE_WAREHOUSE]:        'Warehouse',
  [TaskCodeRegistry.SUPPLY_SOURCE_VENDOR_TRUCK]:     'Vendor Truck',
  [TaskCodeRegistry.SUPPLY_SOURCE_PURCHASED_ONSITE]: 'Purchased On-site',
  [TaskCodeRegistry.SUPPLY_SOURCE_OTHER]:            'Other',
};

const VENDOR_PAY_LABELS: Record<number, string> = {
  [TaskCodeRegistry.VENDOR_PAY_UNPAID]:    'Unpaid',
  [TaskCodeRegistry.VENDOR_PAY_SCHEDULED]: 'Scheduled',
  [TaskCodeRegistry.VENDOR_PAY_PAID]:      'Paid',
};

const EVENT_LABELS: Record<number, string> = {
  [TaskCodeRegistry.EVENT_STARTED]:             'Started',
  [TaskCodeRegistry.EVENT_PAUSED]:              'Paused',
  [TaskCodeRegistry.EVENT_RESUMED]:             'Resumed',
  [TaskCodeRegistry.EVENT_COMPLETED]:           'Completed',
  [TaskCodeRegistry.EVENT_REOPENED]:            'Reopened',
  [TaskCodeRegistry.EVENT_CANCELLED]:           'Cancelled',
  [TaskCodeRegistry.EVENT_TRIAGED]:             'Triaged',
  [TaskCodeRegistry.EVENT_ON_SITE]:             'On Site',
  [TaskCodeRegistry.EVENT_DIAGNOSED]:           'Diagnosed',
  [TaskCodeRegistry.EVENT_AWAITING_PARTS]:      'Awaiting Parts',
  [TaskCodeRegistry.EVENT_AWAITING_VENDOR]:     'Awaiting Vendor',
  [TaskCodeRegistry.EVENT_RESOLVED]:            'Resolved',
  [TaskCodeRegistry.EVENT_VERIFIED_BY_GUEST]:   'Verified by Guest',
  [TaskCodeRegistry.EVENT_ASSIGNMENT_ACCEPTED]: 'Assignment Accepted',
  [TaskCodeRegistry.EVENT_ASSIGNMENT_DECLINED]: 'Assignment Declined',
};

const SOURCE_LABELS: Record<number, string> = {
  [TaskCodeRegistry.SOURCE_INTERNAL]:  'Internal',
  [TaskCodeRegistry.SOURCE_BREEZEWAY]: 'Breezeway',
  [TaskCodeRegistry.SOURCE_BACKFILL]:  'Backfill',
};

const PHOTO_LABELS: Record<number, string> = {
  [TaskCodeRegistry.PHOTO_BEFORE]:    'Before',
  [TaskCodeRegistry.PHOTO_DURING]:    'During',
  [TaskCodeRegistry.PHOTO_AFTER]:     'After',
  [TaskCodeRegistry.PHOTO_REFERENCE]: 'Reference',
  [TaskCodeRegistry.PHOTO_EVIDENCE]:  'Evidence',
  [TaskCodeRegistry.PHOTO_RECEIPT]:   'Receipt',
};

const REGION_LABELS: Record<number, string> = {
  [TaskCodeRegistry.REGION_HAWAII]: 'Hawaii',
  [TaskCodeRegistry.REGION_UTAH]:   'Utah',
  [TaskCodeRegistry.REGION_BOTH]:   'Both',
};

const VENDOR_TYPE_LABELS: Record<number, string> = {
  [TaskCodeRegistry.VENDOR_TYPE_HOUSEKEEPING]:  'Housekeeping',
  [TaskCodeRegistry.VENDOR_TYPE_MAINTENANCE]:   'Maintenance',
  [TaskCodeRegistry.VENDOR_TYPE_INSPECTOR]:     'Inspector',
  [TaskCodeRegistry.VENDOR_TYPE_LANDSCAPING]:   'Landscaping',
  [TaskCodeRegistry.VENDOR_TYPE_PEST_CONTROL]:  'Pest Control',
  [TaskCodeRegistry.VENDOR_TYPE_POOL_SPA]:      'Pool / Spa',
  [TaskCodeRegistry.VENDOR_TYPE_HVAC]:          'HVAC',
  [TaskCodeRegistry.VENDOR_TYPE_PLUMBING]:      'Plumbing',
  [TaskCodeRegistry.VENDOR_TYPE_ELECTRICAL]:    'Electrical',
  [TaskCodeRegistry.VENDOR_TYPE_APPLIANCE]:     'Appliance',
  [TaskCodeRegistry.VENDOR_TYPE_LOCKSMITH]:     'Locksmith',
  [TaskCodeRegistry.VENDOR_TYPE_LAUNDRY]:       'Laundry',
  [TaskCodeRegistry.VENDOR_TYPE_OTHER]:         'Other',
};

const PAYMENT_LABELS: Record<number, string> = {
  [TaskCodeRegistry.PAYMENT_ACH]:         'ACH',
  [TaskCodeRegistry.PAYMENT_CHECK]:       'Check',
  [TaskCodeRegistry.PAYMENT_CREDIT_CARD]: 'Credit Card',
  [TaskCodeRegistry.PAYMENT_OTHER]:       'Other',
};

const TRIGGER_LABELS: Record<number, string> = {
  [TaskCodeRegistry.TRIGGER_RESERVATION_CHECKOUT]:        'Reservation Checkout',
  [TaskCodeRegistry.TRIGGER_RESERVATION_CHECKIN_T_MINUS]: 'Reservation Check-in T-minus',
  [TaskCodeRegistry.TRIGGER_OCCUPANCY_THRESHOLD]:         'Occupancy Threshold',
  [TaskCodeRegistry.TRIGGER_GUEST_PORTAL_REQUEST]:        'Guest Portal Request',
  [TaskCodeRegistry.TRIGGER_INSPECTION_FAILED]:           'Inspection Failed',
  [TaskCodeRegistry.TRIGGER_MANUAL]:                      'Manual',
  [TaskCodeRegistry.TRIGGER_RECURRING_CALENDAR]:          'Recurring Calendar',
  [TaskCodeRegistry.TRIGGER_TASK_CHAIN]:                  'Task Chain',
};

type Domain =
  | 'SUB_STATUS' | 'PRIORITY' | 'BILLABLE' | 'CAUSE' | 'OWNER_APPROVAL'
  | 'LINE_TYPE' | 'SUPPLY_SOURCE' | 'VENDOR_PAY' | 'EVENT' | 'SOURCE'
  | 'PHOTO' | 'REGION' | 'VENDOR_TYPE' | 'PAYMENT' | 'TRIGGER';

const DOMAIN_MAP: Record<Domain, Record<number, string>> = {
  SUB_STATUS:     SUB_STATUS_LABELS,
  PRIORITY:       PRIORITY_LABELS,
  BILLABLE:       BILLABLE_LABELS,
  CAUSE:          CAUSE_LABELS,
  OWNER_APPROVAL: OWNER_APPROVAL_LABELS,
  LINE_TYPE:      LINE_TYPE_LABELS,
  SUPPLY_SOURCE:  SUPPLY_SOURCE_LABELS,
  VENDOR_PAY:     VENDOR_PAY_LABELS,
  EVENT:          EVENT_LABELS,
  SOURCE:         SOURCE_LABELS,
  PHOTO:          PHOTO_LABELS,
  REGION:         REGION_LABELS,
  VENDOR_TYPE:    VENDOR_TYPE_LABELS,
  PAYMENT:        PAYMENT_LABELS,
  TRIGGER:        TRIGGER_LABELS,
};

/**
 * Returns the display label for an integer code within a given domain.
 * Returns the raw value as string if the code is not found.
 */
export function getLabel(domain: Domain, value: number): string {
  const map = DOMAIN_MAP[domain];
  return (map && map[value] != null) ? map[value] : String(value);
}

/** Returns all options for a domain as { value, label } pairs for PrimeNG dropdowns. */
export function getDomainOptions(domain: Domain): { value: number; label: string }[] {
  const map = DOMAIN_MAP[domain];
  return Object.keys(map).map(k => ({ value: Number(k), label: map[Number(k)] }));
}
